import { Button as AntButton } from 'antd';
import styled, { keyframes, css } from 'styled-components';

interface StyledButtonProps {
  maxwidth?: string;
  height?: string;
  radius?: string;
}

/** The look of a lookup field's trigger, shared by the button and the `LookupFieldRow` twin. */
const lookupTriggerStyles = css`
  height: auto;
  min-height: 44px;
  padding: 9px 10px;
  justify-content: flex-start;
  .preselection-field {
    span {
      display: inline-block;
      line-height: 20px;
      color: var(--clr-general-description);
      font-size: 13px;
      font-weight: 400;
    }
    .icon {
      font-size: 18px;
    }
  }
  .selected-values {
    gap: 2px;
    .close-icon {
      min-width: 18px;
      background: transparent;
      font-size: 20px;
      color: var(--clr-general-description);
      cursor: pointer;
      flex-shrink: 0;
    }
    .icon {
      color: var(--clr-general-description);
      font-size: 20px;
      display: block;
      width: 20px;
      height: 20px;
    }
  }
  &:not(:disabled):not(.ant-btn-disabled):hover {
    border-color: var(--clr-general-border-hover);
    color: inherit;
    background-color: var(--clr-general-hover);
  }
  /* The search and clear controls are siblings rather than children — nesting a <button> in a
     <button> is invalid HTML — so the trigger reserves the room they overlap. The search control
     leaves 15px for the chevron, which stays inside the trigger. */
  &:has(+ .lookup-clear-btn) {
    padding-right: 40px;
  }
  &:has(+ .lookup-search-btn) {
    padding-right: 43px;
  }
  &:has(+ .lookup-search-btn + .lookup-clear-btn) {
    padding-right: 73px;
  }
`;

export const Button = styled(AntButton)<StyledButtonProps>`
  &.logout-btn {
    .ant-btn-icon {
      font-size: 18px;
      color: var(--clr-primary-error);
    }
  }
  max-width: ${({ maxwidth }) => maxwidth || 'none'};
  height: ${({ height }) => height || 'none'};
  border-radius: ${({ radius }) => radius || 'none'};
  &.filter-cta {
    padding-inline: 0;
    font-size: 12px;
    &:disabled {
      color: rgba(var(--clr-general-primary-rgb), 0.6);
    }
  }
  &.important-note-btn {
    border: none;
    width: 18px;
    height: 18px;
    background: transparent;
    .ant-btn-icon {
      font-size: 18px;
      color: var(--clr-error-100);
    }
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: transparent;
    }
  }
  &.add-transaction-btn {
    ${lookupTriggerStyles}
    /* Doubling the class keeps these above antd's own .ant-btn:disabled rule.
       A lookup button counts as filled unless it is showing its placeholder —
       some render a value without a .selected-values wrapper. */
    &.add-transaction-btn:disabled {
      background-color: var(--clr-container-bg);
      opacity: 1;
    }
    &.add-transaction-btn:disabled:not(:has(.preselection-field)) {
      background-color: var(--clr-field-disabled);
      opacity: 1;
    }
  }
  &.change-value-btn {
    font-size: 12px;
    height: 18px;
    padding: 0;
    position: absolute;
    transform: translate(-50%, -50%);
    right: -21px;
    top: -16px;
  }
  &.need-suggestion-btn {
    right: -56px;
  }
  &.favourite-cta {
    color: var(--clr-subtitle);
    padding: 4px 4px 4px 10px;
    background: rgba(var(--clr-general-dark-grey-rgb), 0.05);
    backdrop-filter: blur(40px);
    &.active {
      background: rgba(var(--clr-general-primary-rgb), 0.2);
      &:not(:disabled):not(.ant-btn-disabled):hover {
        background: rgba(var(--clr-general-primary-rgb), 0.2);
      }
    }
    span {
      font-size: 12px;
      color: var(--clr-subtitle);
      line-height: 20px;
    }
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background: rgba(var(--clr-general-dark-grey-rgb), 0.05);
      color: var(--clr-subtitle);
    }
  }
  &.ant-btn-default {
    &:disabled {
      background-color: var(--clr-container-bg);
      color: var(--clr-subtitle);
      border-color: var(--clr-border-grey-100);
      opacity: 0.4;
    }
  }
  &.ant-btn-default.ant-btn-dangerous {
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: var(--clr-general-hover);
      border-color: var(--clr-general-border-hover);
      color: var(--clr-general-primary);
    }
    &:not(:disabled):not(.ant-btn-disabled):active {
      background-color: var(--selected);
      border-color: var(--clr-general-primary);
      color: var(--clr-general-primary);
    }
  }
  &.ant-btn-color-primary.ant-btn-variant-outlined {
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: var(--clr-general-hover);
      border-color: var(--clr-general-border-hover);
      color: var(--clr-general-primary);
    }
    &:not(:disabled):not(.ant-btn-disabled):active {
      background-color: var(--selected);
      border-color: var(--clr-general-primary);
      color: var(--clr-general-primary);
    }
  }
  &.text-cta {
    padding: 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    gap: 2px;
    height: 18px;
    .ant-btn-icon {
      .icon {
        font-size: 14px;
        flex-shrink: 0;
        color: var(--clr-general-primary);
        opacity: 1;
      }
    }
  }
  &.refresh-btn {
    color: var(--clr-general-description);
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: var(--clr-general-hover);
      border-color: var(--clr-general-border-hover);
      color: var(--clr-general-primary);
    }
    &:not(:disabled):not(.ant-btn-disabled):active {
      background-color: var(--selected);
      border-color: var(--clr-general-primary);
      color: var(--clr-general-primary);
    }
  }
`;

/**
 * A lookup field trigger whose value is made of its own controls — removable tags, say. Those
 * controls are buttons, so the trigger around them cannot be one too. Callers give it the
 * `add-transaction-btn` class and button semantics, and it repeats the box `.ant-btn` supplies.
 */
export const LookupFieldRow = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  width: 100%;
  gap: 8px;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: var(--clr-subtitle);
  background: var(--clr-container-bg);
  border: 1px solid var(--clr-border-grey-100);
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  ${lookupTriggerStyles}
`;

export const LoginButton = styled(AntButton)`
  width: 100%;
  padding: 10px 20px;
  border-radius: 5px;
  background-color: var(--clr-white);
  color: var(--clr-general-primary);
  font-size: 12px;
  font-weight: 500;
  height: 36px;
  transition: all 0.5s;
  border-color: transparent;
  cursor: pointer;
  border: 0;
  &:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: var(--clr-general-hover);
    color: var(--clr-general-primary);
  }
  &:disabled {
    background-color: var(--clr-white);
    color: var(--clr-general-primary);
    opacity: 0.8;
    cursor: not-allowed;
  }
`;

export const IconButton = styled(AntButton)`
  height: 24px;
  border-radius: 5px;
  border: 0;
  flex-shrink: 0;
  background-color: transparent;
  &.ant-btn-icon-only {
    width: 24px;
  }
  &.ant-btn-lg {
    height: 30px;
    &.ant-btn-icon-only {
      width: 30px;
    }
    .ant-btn-icon {
      font-size: 20px;
    }
  }
  .ant-btn-icon {
    font-size: 18px;
    color: var(--clr-general-description);
    .close-btn-icon {
      font-size: 12px;
    }
    .delete-icon {
      color: var(--clr-primary-error);
    }
  }
  &:not(:disabled):not(.ant-btn-disabled):hover {
    background: var(--clr-general-hover);
  }
  &.clear-icon-btn {
    width: 18px;
    height: 18px;
    padding: 0;
    .icon {
      font-size: 18px;
      color: var(--clr-general-description);
    }
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background: transparent;
    }

    &.clear-icon-btn:disabled,
    &.clear-icon-btn.ant-btn-disabled {
      background: transparent;
    }
  }
  /* Sits over the right edge of the lookup trigger it follows, matching that button's padding. */
  &.lookup-clear-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }
  /* Sits over the right edge of the lookup trigger it follows, and moves left of the clear control when one follows it. */
  &.lookup-search-btn {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    padding: 0;
    &:has(+ .lookup-clear-btn) {
      right: 40px;
    }
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background: transparent;
      .ant-btn-icon {
        color: var(--clr-general-primary);
      }
    }
    &:disabled,
    &.ant-btn-disabled {
      background: transparent;
    }
  }
  &.favourite-icon {
    width: 24px;
    height: 24px;
    padding: 0;
    .icon {
      font-size: 18px;
      color: var(--clr-general-description);
    }
    &.active {
      &:not(:disabled):not(.ant-btn-disabled):hover {
        background: transparent;
      }
      .icon {
        color: var(--clr-general-primary);
      }
    }
  }
  &.timer-active {
    background: var(--clr-general-hover);
  }
  &.page-active {
    background-color: var(--clr-general-primary);
    .ant-btn-icon {
      color: var(--clr-white);
    }

    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: var(--clr-general-primary);
      .ant-btn-icon {
        color: var(--clr-white);
      }
    }
  }
  &.notification-btn {
    &.has-notification {
      &::after {
        content: '';
        width: 10px;
        height: 10px;
        border-radius: 50px;
        background-color: var(--clr-error-100);
        border: 2px solid var(--clr-container-bg);
        position: absolute;
        transform: translate(-50%, -50%);
        right: -3px;
        top: 8px;
      }
    }
    &.page-active {
      &::after {
        display: none;
      }
    }
  }
`;

const settingRowStyles = css`
  padding: 10px 15px;
  height: auto;
  align-items: flex-start;
  transition: all 0.2s linear;
  .start-icon {
    font-size: 20px;
    color: var(--clr-general-primary);
    flex-shrink: 0;
    .logout {
      color: var(--clr-primary-error);
    }
  }
  .end-icon {
    font-size: 20px;
    color: var(--clr-general-description);
    flex-shrink: 0;
  }
  &.profile-about-btn {
    height: 48px;
    align-items: center;
  }
`;

export const SettingButton = styled(AntButton)`
  ${settingRowStyles}
  &:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: var(--clr-general-hover);
    border-color: var(--clr-general-border-hover);
  }
`;

/**
 * A settings row that only carries a control instead of being clickable itself. antd's `Switch`
 * renders a `<button>`, so the row around it cannot be one too. It repeats the box `.ant-btn`
 * gives `SettingButton`, without the hover tint a non-clickable row should not have.
 */
export const SettingRow = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  gap: 8px;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  color: var(--clr-subtitle);
  background: var(--clr-container-bg);
  border: 1px solid var(--clr-border-grey-100);
  border-radius: 5px;
  ${settingRowStyles}
`;

/** Groups consecutive `SettingButton` rows into a single card, as the settings screens show them. */
export const SettingButtonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  .profile-about-btn {
    border-radius: 0;
    /* Rows pull up by their border width so neighbours share one hairline. */
    margin-bottom: -1px;
    position: relative;
    /* Keyed on child position: a row holding a control is a div among buttons, so
       :first-of-type would round both. */
    &:first-child {
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
    }
    &:last-child {
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
      margin-bottom: 0;
    }
    /* Lifted so the hover tint is not clipped by the neighbour overlapping the shared hairline. */
    &:hover {
      z-index: 1;
    }
  }
`;

export const DefaultIconButton = styled(AntButton)`
  &.ant-btn-icon-only {
    width: 20px;
    height: 20px;
  }
  padding: 0;
  .ant-btn-icon {
    font-size: 16px;
    color: var(--clr-general-description);
  }
  &.attachment-cta {
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: var(--clr-general-hover);
    }
  }
  &.reset-btn {
    width: 24px;
    height: 24px;
    position: absolute;
    bottom: 73px;
    max-height: 24px;
    right: 13px;
    color: var(--clr-general-description);
    .icon {
      font-size: 16px;
      flex-shrink: 0;
    }
    &::before {
      content: '';
      width: 1px;
      height: 25px;
      background-color: var(--clr-border-grey-100);
      flex-shrink: 0;
    }
  }
`;

export const IconOutlineButton = styled(AntButton)`
  width: 100%;
  max-width: 30px;
  flex-shrink: 0;
  .ant-btn-icon {
    font-size: 18px;
    color: var(--clr-general-description);
  }
  &.ant-btn-variant-outlined:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: var(--clr-general-hover);
    border-color: var(--clr-general-border-hover);
    .ant-btn-icon {
      color: var(--clr-general-primary);
    }
  }
  &.filter-button {
    position: relative;
    background-color: var(--selected);
    border-color: var(--clr-general-primary);
    .ant-btn-icon {
      color: var(--clr-general-primary);
    }
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: var(--selected-hover);
      border-color: var(--clr-general-primary);
    }
    &::after {
      content: '';
      width: 10px;
      height: 10px;
      border-radius: 50px;
      background-color: var(--clr-error-100);
      border: 1px solid var(--selected);
      position: absolute;
      transform: translate(-50%, -50%);
      right: -1px;
      top: 8px;
    }
  }
  &.active {
    background-color: var(--clr-general-primary);
    border-color: var(--clr-general-primary);
    .ant-btn-icon {
      color: var(--clr-white);
    }
    &:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: var(--clr-general-primary);
      border-color: var(--clr-general-primary);
      .ant-btn-icon {
        color: var(--clr-white);
      }
    }
  }
  &.filter-button.active::after {
    border-color: var(--clr-container-bg);
  }
  /* Declared after .filter-button so a locked trigger drops its selected look and reads as disabled. */
  &:disabled,
  &.ant-btn-disabled {
    border-color: var(--clr-border-grey-100);
    .ant-btn-icon {
      color: var(--clr-general-description);
      opacity: 0.5;
    }
  }
  /* A locked trigger that still carries applied filters keeps its selected look and fades as one
     element, so the icon and the dot dim together instead of each on their own. */
  &.filter-button:disabled,
  &.filter-button.ant-btn-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: var(--selected);
    border-color: var(--clr-general-primary);
    .ant-btn-icon {
      color: var(--clr-general-primary);
      opacity: 1;
    }
  }
  &:not(.filter-button):not(.active):not(.suggested-matter-action):not(:disabled):not(
      .ant-btn-disabled
    ):active {
    background-color: var(--selected);
    border-color: var(--clr-general-primary);
    .ant-btn-icon {
      color: var(--clr-general-primary);
    }
  }
  /* The suggested matter panel paints its own tint, so its header controls are bare icons. */
  &.suggested-matter-action {
    border: 0;
    padding: 0;
    width: 16px;
    height: 16px;
    background-color: transparent;
    .ant-btn-icon {
      font-size: 16px;
    }
    .icon {
      display: flex;
    }
    /* a bare icon has no frame to light up, so it stays as it is under the pointer */
    &.ant-btn-variant-outlined:not(:disabled):not(.ant-btn-disabled):hover {
      background-color: transparent;
      .ant-btn-icon {
        color: var(--clr-general-description);
      }
    }
  }
`;

export const CardCta = styled(AntButton)`
  padding: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  flex-direction: column;
  min-height: 45px;
  height: 100%;
  border: 0;
  transition: all 0.2s linear;
  .icon {
    font-size: 22px;
    color: var(--clr-general-description);
  }
  p {
    display: flex;
    justify-content: center;
    align-items: center;
    text-wrap: wrap;
    flex-grow: 1;
  }
  &:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: var(--clr-general-hover);
    p {
      color: var(--clr-title);
    }
    .icon {
      color: var(--clr-general-primary);
    }
  }
`;

const shake = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
`;

export const CustomActionButton = styled(AntButton)`
  border-radius: 0 5px 5px 0;
  position: fixed;
  z-index: 2;
  left: -3px;
  width: 26px;
  background-color: transparent;
  padding: 0;
  height: 64px;
  opacity: 60%;
  transition: opacity 0.3s ease;
  cursor: pointer !important;

  .icon {
    color: var(--clr-general-primary);
  }
  &.shaking {
    animation: ${shake} 0.2s infinite;
  }
  /* The button hangs off the pane's left edge, so it moves fully on screen while focused. */
  &&:focus-visible {
    left: 0;
    opacity: 1;
    outline-offset: -2px;
  }
  &:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: transparent;

    opacity: 1;
    .icon {
      color: var(--clr-general-primary);
    }
  }
`;

interface DraggableContainerProps {
  $position: number;
  $isDragging: boolean;
}

export const DraggableContainer = styled.div<DraggableContainerProps>`
  position: fixed;
  left: 0;
  top: ${(props) => props.$position}px;
  z-index: 5;
  cursor: pointer !important;
`;

const moveUp = keyframes`
  0% {
    transform:  translateY(-12px)  ;
  }
  24% {
    transform:  translateY(0px)  ;
  }
  50% {
    transform:  translateY(-12px)  ;
  }
  74% {
    transform:  translateY(0px)  ;
  }
  100% {
    transform:  translateY(-12px)  ;
  }
`;

const moveDown = keyframes`
 0% {
    transform:  translateY(12px)  ;
  }
  24% {
    transform:  translateY(0px)  ;
  }
  50% {
    transform:  translateY(12px)  ;
  }
  74% {
    transform:  translateY(0px)  ;
  }
  100% {
    transform:  translateY(12px)  ;
  }
`;

export const Arrow = styled.span<{ direction: 'up' | 'down' }>`
  position: absolute;
  ${(p) => (p.direction === 'up' ? 'top: -14px;' : 'bottom: -78px;')}
  width: 18px;
  height: 18px;
  left: 1px;
  .icon,
  svg {
    display: block;
  }
  opacity: 1;
  animation: ${(p) =>
    p.direction === 'up'
      ? css`
          ${moveUp} 2.8s linear infinite
        `
      : css`
          ${moveDown} 2.8s linear infinite
        `};
`;

/** A card's chevron or status star: the card itself is the control, so this is no extra tab stop. */
export const DecorativeIconButton = styled(IconButton).attrs({
  tabIndex: -1,
  'aria-hidden': true,
})``;
